<template>
  <div class="new-validity-ranges">
    <div class="new-validity-ranges__head">
      <span class="new-validity-ranges__title">{{ title }}</span>
      <a-tooltip :title="canAdd ? '' : MAX_RANGES_MESSAGE" placement="topRight">
        <a-button
          type="link"
          class="new-validity-ranges__add"
          :disabled="!canAdd"
          @click="addRange"
        >
          <a-icon type="plus" /> Nova faixa
        </a-button>
      </a-tooltip>
    </div>

    <div
      v-for="(faixa, index) in value"
      :key="index"
      class="new-validity-ranges__range"
    >
      <div class="new-validity-ranges__range-head">
        <span class="new-validity-ranges__range-title">Faixa {{ index + 1 }}</span>
        <a-button
          v-if="value.length > 1"
          type="link"
          class="new-validity-ranges__trash"
          :aria-label="`Remover faixa ${index + 1}`"
          @click="removeRange(index)"
        >
          <a-icon type="delete" />
        </a-button>
      </div>

      <div class="new-validity-ranges__grid">
        <div
          v-for="field in RANGE_FIELDS"
          :key="field.key"
          class="new-validity-ranges__field"
          :class="{ 'new-validity-ranges__field--error': errorOf(index, field.key) }"
        >
          <span class="new-validity-ranges__label">
            <span v-if="field.required" class="new-validity-ranges__required">*</span>
            {{ field.label }}
          </span>
          <a-input
            :value="field.currency ? formatCurrency(faixa[field.key]) : formatDecimal(faixa[field.key])"
            :placeholder="field.placeholder"
            @change="(e) => onNumberChange(index, field.key, e)"
          />
          <p v-if="errorOf(index, field.key)" class="new-validity-ranges__error">
            {{ errorOf(index, field.key) }}
          </p>
        </div>
      </div>

      <div class="new-validity-ranges__flags">
        <a-checkbox
          :checked="isOn(faixa.fixa)"
          @change="(e) => onFlagChange(index, 'fixa', e.target.checked)"
        >
          Fixa
        </a-checkbox>
        <a-checkbox
          :checked="isOn(faixa.excedente)"
          @change="(e) => onFlagChange(index, 'excedente', e.target.checked)"
        >
          Excedente
        </a-checkbox>
      </div>

      <div
        v-if="isOn(faixa.excedente)"
        class="new-validity-ranges__field new-validity-ranges__excedente"
        :class="{ 'new-validity-ranges__field--error': errorOf(index, 'vlr_exd') }"
      >
        <span class="new-validity-ranges__label">
          <span class="new-validity-ranges__required">*</span> Valor excedente
        </span>
        <a-input
          :value="formatCurrency(faixa.vlr_exd)"
          placeholder="Informe o valor"
          @change="(e) => onNumberChange(index, 'vlr_exd', e)"
        />
        <p v-if="errorOf(index, 'vlr_exd')" class="new-validity-ranges__error">
          {{ errorOf(index, "vlr_exd") }}
        </p>
      </div>

      <p
        v-for="message in otherErrors(index)"
        :key="message"
        class="new-validity-ranges__error"
      >
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script>
import { createEmptyFaixa } from "../../services/freightTableConfigForm";
import { NEW_VALIDITY_MAX_RANGES } from "../../utils/newValidityForm";

const RANGE_FIELDS = [
  { key: "vlr_ini", label: "Faixa inicial", placeholder: "0,00", required: true },
  { key: "vlr_fim", label: "Faixa final", placeholder: "0,00", required: true },
  { key: "vlr", label: "Valor", placeholder: "R$ 0,00", required: true, currency: true },
  { key: "fator_conv", label: "Fator conversão", placeholder: "1,00" },
];
const SHOWN_FIELDS = ["vlr_ini", "vlr_fim", "vlr", "fator_conv", "vlr_exd"];
const MAX_RANGES_MESSAGE = `É possível informar até ${NEW_VALIDITY_MAX_RANGES} faixas por vigência.`;

export default {
  name: "FreightTableNewValidityRanges",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: "Faixas de cálculo",
    },
  },
  data() {
    return { RANGE_FIELDS, MAX_RANGES_MESSAGE };
  },
  computed: {
    canAdd() {
      return this.value.length < NEW_VALIDITY_MAX_RANGES;
    },
  },
  methods: {
    isOn(value) {
      return Number(value) === 1;
    },
    errorOf(index, field) {
      const range = this.errors[index];
      return range ? range[field] || "" : "";
    },
    otherErrors(index) {
      const range = this.errors[index] || {};
      return Object.keys(range)
        .filter((field) => SHOWN_FIELDS.indexOf(field) === -1)
        .map((field) => range[field]);
    },
    formatDecimal(value) {
      if (value === null || value === undefined || value === "") return "";
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      return number.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "";
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      return number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    readDigits(e) {
      const raw = String((e && e.target ? e.target.value : e) || "");
      const digits = raw.replace(/\D/g, "");
      return digits === "" ? null : Number(digits) / 100;
    },
    patchRange(index, patch) {
      this.$emit(
        "update:value",
        this.value.map((faixa, i) => (i === index ? { ...faixa, ...patch } : faixa)),
      );
    },
    onNumberChange(index, field, e) {
      this.patchRange(index, { [field]: this.readDigits(e) });
    },
    onFlagChange(index, field, checked) {
      const patch = { [field]: checked ? 1 : 0 };
      if (field === "excedente" && !checked) patch.vlr_exd = null;
      this.patchRange(index, patch);
    },
    addRange() {
      if (!this.canAdd) return;
      const previous = this.value[this.value.length - 1] || {};
      const next = createEmptyFaixa();
      if (previous.vlr_fim !== null && previous.vlr_fim !== undefined && previous.vlr_fim !== "") {
        next.vlr_ini = Number(previous.vlr_fim);
      }
      this.$emit("update:value", this.value.concat([next]));
    },
    removeRange(index) {
      if (this.value.length <= 1) return;
      this.$emit(
        "update:value",
        this.value.filter((_, i) => i !== index),
      );
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.new-validity-ranges {
  margin-bottom: 16px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: @color-text-dark;
  }

  &__add {
    padding: 0 8px;
    color: @color-primary;

    &:hover,
    &:focus {
      color: @color-primary-hover;
    }
  }

  &__range {
    padding: 12px;
    margin-bottom: 8px;
    background: @color-bg-surface;
    border: 1px solid @color-border-light;
    border-radius: 8px;
  }

  &__range-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 24px;
    margin-bottom: 8px;
  }

  &__range-title {
    font-size: 13px;
    font-weight: 500;
    color: @color-text-medium;
  }

  &__trash {
    height: 24px;
    padding: 0;
    color: @color-danger;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--error /deep/ .ant-input {
      border-color: @color-danger;
    }
  }

  &__label {
    display: block;
    padding-bottom: 4px;
    font-size: 13px;
    line-height: 20px;
    color: @color-text-dark;
  }

  &__required {
    margin-right: 2px;
    color: @color-danger;
  }

  &__flags {
    display: flex;
    gap: 16px;
    margin-top: 12px;
  }

  &__excedente {
    margin-top: 8px;
  }

  &__error {
    margin: 4px 0 0 0;
    font-size: 12px;
    line-height: 18px;
    color: @color-danger;
    white-space: pre-line;
  }
}
</style>
